
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, Users, Calendar, MessageSquare, Star, TrendingUp } from 'lucide-react';
import PermissionGate from '@/components/rbac/PermissionGate';
import { useFanTeams, useUserTeams } from '@/hooks/useUserRelationships';
import { useFanActivity } from '@/hooks/useFanActivity';
import { useGames } from '@/hooks/useGames';
import { format } from 'date-fns';

const FanDashboard: React.FC = () => {
  const formatGameTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      
      const formattedTime = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `${formattedTime} CST`;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time;
    }
  };

  const { fanTeams, loading: fanTeamsLoading } = useFanTeams();
  const { stats: fanActivityStats, loading: activityLoading } = useFanActivity();
  const { upcomingGames, userGames, loading: gamesLoading } = useGames();

  // Use user games if available, otherwise show general upcoming games
  const displayGames = userGames.length > 0 ? userGames : upcomingGames;
  const totalUpcomingGames = displayGames.filter(game => 
    new Date(game.game_date) > new Date()
  ).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <PermissionGate permission="follow_teams">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Heart className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Following</h4>
                <p className="text-sm text-gray-300">
                  {fanTeamsLoading ? 'Loading...' : `${fanTeams.length} teams`}
                </p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_schedules">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <Calendar className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Upcoming Games</h4>
                <p className="text-sm text-gray-300">
                  {gamesLoading ? 'Loading...' : `${totalUpcomingGames} this week`}
                </p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="comment_content">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-4 flex items-center space-x-4">
              <MessageSquare className="h-8 w-8 text-sports-green" />
              <div>
                <h4 className="font-semibold text-white">Comments</h4>
                <p className="text-sm text-gray-300">
                  {activityLoading ? 'Loading...' : `${fanActivityStats.comments} this month`}
                </p>
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PermissionGate permission="follow_teams">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Users className="h-5 w-5 text-sports-green" />
                My Teams
              </h4>
              <div className="space-y-3">
                {fanTeamsLoading ? (
                  <div className="text-gray-400 text-center">Loading teams...</div>
                ) : fanTeams.length > 0 ? (
                  fanTeams.slice(0, 3).map((fanTeam) => (
                    <div key={fanTeam.team_id} className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-sports-green rounded-full flex items-center justify-center">
                          <span className="text-black font-bold text-sm">
                            {fanTeam.team?.team_name?.charAt(0) || 'T'}
                          </span>
                        </div>
                        <div>
                          <span className="text-white font-medium">{fanTeam.team?.team_name || 'Unknown Team'}</span>
                          <p className="text-sm text-gray-300">Next game coming soon</p>
                        </div>
                      </div>
                      <Heart className="h-5 w-5 text-red-400 fill-current" />
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-center">No teams followed yet</div>
                )}
              </div>
            </CardContent>
          </Card>
        </PermissionGate>

        <PermissionGate permission="view_schedules">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-sports-green" />
                This Week's Games
              </h4>
              <div className="space-y-3">
                {gamesLoading ? (
                  <div className="text-gray-400 text-center">Loading games...</div>
                ) : displayGames.length > 0 ? (
                  displayGames.slice(0, 3).map((game) => {
                    const isThisWeek = new Date(game.game_date) > new Date() && 
                      new Date(game.game_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                    
                    if (!isThisWeek) return null;
                    
                    const gameDate = new Date(game.game_date);
                    const gameTime = game.game_time ? new Date(`1970-01-01T${game.game_time}`) : null;
                    
                    return (
                      <div key={game.id} className="p-3 bg-gray-700 rounded-md border-l-4 border-blue-400">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-white font-medium">
                            {game.game_name}
                          </span>
                          <span className="text-sm font-medium text-blue-400">
                            {format(gameDate, 'EEE')}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-300">{game.venue_name || 'TBD'}</span>
                          <span className="text-gray-300">
                            {gameTime ? format(gameTime, 'h:mm a') + ' CST' : formatGameTime(game.game_time)}
                          </span>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)
                ) : (
                  <div className="text-gray-400 text-center">No games scheduled this week</div>
                )}
              </div>
            </CardContent>
          </Card>
        </PermissionGate>
      </div>

      <PermissionGate permission="comment_content">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h4 className="font-semibold mb-4 text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-sports-green" />
              Fan Activity
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-sports-green mb-1">
                  {activityLoading ? '...' : fanActivityStats.likes}
                </div>
                <div className="text-sm text-gray-300">Total Likes Given</div>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-sports-green mb-1">
                  {activityLoading ? '...' : fanActivityStats.comments}
                </div>
                <div className="text-sm text-gray-300">Comments Posted</div>
              </div>
              <div className="text-center p-4 bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-sports-green mb-1">
                  {activityLoading ? '...' : fanActivityStats.shares}
                </div>
                <div className="text-sm text-gray-300">Posts Shared</div>
              </div>
            </div>
            <div className="mt-4 p-4 bg-sports-green/10 border border-sports-green/30 rounded-lg">
              <h5 className="text-sports-green font-medium mb-2">🏆 Fan Level: Super Fan</h5>
              <p className="text-sm text-gray-300">
                You're in the top 10% of most active fans! Keep supporting your teams to unlock special perks.
              </p>
            </div>
          </CardContent>
        </Card>
      </PermissionGate>
    </div>
  );
};

export default FanDashboard;
